import { useEffect, useRef, useState } from 'react';

import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { InfiniteVirtualTable } from '@nesso/ui/blocks/table/infinite-virtual-table';
import { VirtualTableLoadingRow } from '@nesso/ui/blocks/table/virtual-table-loading-row';
import { cn } from '@nesso/ui/lib/utils';
import { NessoSpinner } from '@nesso/ui/primitives/nesso-spinner';

import type {
    PracticeMemberUsageOutput,
    PracticeMemberUsageSortBy,
    PracticeMemberUsageSortDirection,
    PracticeMemberUsageSummary,
} from '@/entities/reporting';

import { buildPracticeMemberUsageTableColumns } from './reporting-page-practice-member-usage-table/reporting-page-practice-member-usage-table-columns';
import { ReportingPagePracticeMemberUsageTableRow } from './reporting-page-practice-member-usage-table/reporting-page-practice-member-usage-table-row';
import { ReportingPagePracticeMemberUsageTableSummaryRow } from './reporting-page-practice-member-usage-table/reporting-page-practice-member-usage-table-summary-row';

// Explicit rather than the component's 36px default; must match the height baked into every
// row/summary/loading-row component here.
const ROW_HEIGHT = 52;

// Reserved via `hasNextPage` mid-fetch so the spinner never shifts scroll height; rendered
// standalone when empty (below).
const FOOTER_HEIGHT = 'h-13';

type Props = {
    search: string | undefined;
    // Composed onto the first page's response (no cursor sent) — undefined on every later page.
    summary: PracticeMemberUsageSummary | undefined;
    practiceMembers: PracticeMemberUsageOutput['practiceMembers'];
    sortBy: PracticeMemberUsageSortBy;
    sortDirection: PracticeMemberUsageSortDirection;
    onSortChange: (column: PracticeMemberUsageSortBy) => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    error: Error | null;
};

export function ReportingPagePracticeMemberUsageTable({
    search,
    summary,
    practiceMembers,
    sortBy,
    sortDirection,
    onSortChange,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    error,
}: Props) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    // Drives the pinned Name column's shadow while scrolled horizontally.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs on the same key as InfiniteVirtualTable's remount below, so the listener isn't left on a detached scroll div after sort/search.
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => setIsScrolled(scrollContainer.scrollLeft > 0);
        handleScroll();
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [search, sortBy, sortDirection]);

    const table = useReactTable({
        data: practiceMembers,
        columns: buildPracticeMemberUsageTableColumns({
            sortBy,
            sortDirection,
            onSortChange,
            isScrolled,
        }),
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.practiceMemberId,
    });
    const columns = table.getAllColumns();
    const isEmpty = practiceMembers.length === 0;

    return (
        <>
            <InfiniteVirtualTable
                // Keyed on the query's identity so a shorter result after a new search/sort remounts
                // the table instead of leaving the virtualizer an out-of-range scroll offset.
                key={`${search ?? ''}:${sortBy}:${sortDirection}`}
                table={table}
                rows={table.getRowModel().rows}
                scrollContainerRef={scrollContainerRef}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={onLoadMore}
                // Pinned to success: the Alert beside this table already surfaces `error`, so threading
                // the real status would blank the loaded rows on a next-page failure.
                status="success"
                error={error}
                skeleton={<NessoSpinner className="self-center" />}
                loadingRow={
                    <VirtualTableLoadingRow
                        colSpan={columns.length}
                        spinner={<NessoSpinner className="size-5" />}
                        rowClassName="hover:bg-transparent"
                        cellClassName={FOOTER_HEIGHT}
                    />
                }
                // No `flex` here — it would drop the `<td>`'s colSpan width; align-middle centers it.
                emptyStateClassName="h-45 py-6 border-b-0! [&_p]:text-popover-foreground"
                estimateSize={ROW_HEIGHT}
                className="min-h-0 flex-1 overflow-auto bg-background *:data-[slot=table-container]:overflow-visible"
                // `border-separate` (sticky-safe) + `table-fixed`/`min-w-[997px]` (fixed widths) drop
                // `TableRow`'s own border-b; only `td` restores it — `th` has none, per the muted header.
                tableClassName="w-full min-w-[997px] table-fixed border-separate border-spacing-0 [&_td]:border-b"
                subHeaderRow={
                    !isEmpty &&
                    summary && (
                        <ReportingPagePracticeMemberUsageTableSummaryRow
                            summary={summary}
                            isScrolled={isScrolled}
                        />
                    )
                }
                renderRow={({ row }) => (
                    <ReportingPagePracticeMemberUsageTableRow row={row} isScrolled={isScrolled} />
                )}
            />
            {/* The empty state has no next page to reserve this via, so it renders standalone
                rather than through `hasNextPage`'s in-table placeholder. */}
            {isEmpty && <div className={cn('shrink-0 bg-background', FOOTER_HEIGHT)} />}
        </>
    );
}
